<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :draggable="false"
    :dismissable-mask="false"
    :show-header="false"
    class="integration-config-modal"
    :style="{ width: '900px', maxWidth: '95vw' }"
    :breakpoints="{ '960px': '95vw' }"
    @hide="onHide"
  >
    <!-- No Dialog header: IntegrationConfig renders its own step-aware top bar
         (logo + step + step title + close). Content re-mounted per integration
         (`:key`) so it loads fresh state each open. -->
    <IntegrationConfig
      v-if="isVisible && slug"
      :key="slug"
      :slug="slug"
      :as-modal="true"
      :initial-name="name"
      :initial-icon="icon"
      @close="close"
      @done="onDone"
    />
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import Dialog from 'primevue/dialog';
import IntegrationConfig from '@/views/shop/IntegrationConfig.vue';
import type { Integration } from '@/composables/useIntegrations';

export default defineComponent({
  name: 'IntegrationConfigModal',

  components: {
    Dialog,
    IntegrationConfig,
  },

  emits: ['done'],

  setup(_, { emit, expose }) {
    const isVisible = ref(false);
    const slug = ref('');
    const name = ref('');
    const icon = ref('');

    /** Open the config modal for a given (installed) integration. */
    const open = (integration: Integration) => {
      slug.value = integration.slug;
      name.value = integration.name || '';
      icon.value = integration.icon_url || '';
      isVisible.value = true;
    };

    /** Close without signalling completion (cancel). */
    const close = () => {
      isVisible.value = false;
    };

    /** Configuration finished successfully → tell the parent to refresh. */
    const onDone = () => {
      isVisible.value = false;
      emit('done');
    };

    /** Reset transient state once the dialog is fully hidden. */
    const onHide = () => {
      slug.value = '';
      name.value = '';
      icon.value = '';
    };

    expose({ open });

    return {
      isVisible,
      slug,
      name,
      icon,
      close,
      onDone,
      onHide,
    };
  },
});
</script>

<style lang="scss">
/* Unscoped: tune the PrimeVue Dialog shell for the config wizard. No Dialog
   header — IntegrationConfig provides its own step-aware top bar. */
.integration-config-modal {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e4e7ec;
  box-shadow: 0 12px 40px rgba(16, 24, 40, 0.10), 0 2px 6px rgba(16, 24, 40, 0.05);

  .p-dialog-content {
    /* `!important` : le thème PrimeVue applique un padding sur
       `.p-dialog .p-dialog-content` à spécificité égale — on le neutralise ici
       pour que le contenu soit à ras (les zones internes gèrent leur padding). */
    padding: 0 !important;
    background: #ffffff;
    /* Bounded height so the fixed chrome (header + stepper + footer) and the
       scrolling field column have a definite space to distribute. */
    height: clamp(500px, 82vh, 760px);
    overflow: hidden;
  }
}
</style>
